import React, { useEffect, useMemo, useRef, useState } from 'react';
import './BookingAttendance.css';
import { useLocation } from 'react-router-dom';
import casInstance, { attendance_default_event_id, caversion } from '../config/Api';

const initialFormState = {
  eventCode: '',
  scanValue: ''
};

const parseMemberRegNo = (rawValue) => {
  if (!rawValue) return '';
  const trimmed = rawValue.trim();
  if (!trimmed) return '';
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed === 'object') {
      const candidate = parsed.member_reg_no || parsed.memberRegNo || parsed.reg_no;
      if (candidate) return String(candidate).trim();
    }
  } catch (error) {
    // Not JSON; fall back to regex parsing.
  }
  const digits = trimmed.match(/\d+/g);
  return digits ? digits.join('') : trimmed;
};

const formatEventDate = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getBangladeshTime = () =>
  new Date().toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Dhaka'
  });

const BookingAttendance = () => {
  const location = useLocation();
  const [formState, setFormState] = useState(initialFormState);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [checkins, setCheckins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [attendee, setAttendee] = useState(null);
  const [eventId, setEventId] = useState(String(attendance_default_event_id));
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState('');
  const [scannerReady, setScannerReady] = useState(true);
  const [lastScanAt, setLastScanAt] = useState('');
  const inputRef = useRef(null);
  const scanTimeoutRef = useRef(null);

  const totals = useMemo(() => {
    const total = checkins.length;
    const latest = total ? checkins[0] : null;
    return {
      total,
      latestLabel: latest ? `${latest.name || 'Guest'} · ${latest.eventCode}` : 'No check-ins yet'
    };
  }, [checkins]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const focusScanner = () => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  };

  useEffect(() => {
    focusScanner();
  }, []);

  useEffect(() => {
    let isActive = true;
    const fetchStats = async () => {
      setStatsLoading(true);
      setStatsError('');
      try {
        const { data } = await casInstance.get(`${caversion}events/statistics`);
        if (!isActive) return;
        if (data && data.success) {
          setStats(data.data || null);
        } else {
          setStats(null);
          setStatsError(data && data.message ? data.message : 'Unable to load event stats.');
        }
      } catch (error) {
        if (!isActive) return;
        setStats(null);
        setStatsError('Unable to load event stats right now.');
      } finally {
        if (isActive) {
          setStatsLoading(false);
        }
      }
    };

    fetchStats();
    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paramEventId = params.get('event_id');
    if (paramEventId) {
      setEventId(paramEventId);
      localStorage.setItem('attendance_event_id', paramEventId);
      return;
    }
    const storedEventId = localStorage.getItem('attendance_event_id');
    if (storedEventId) {
      setEventId(storedEventId);
    }
  }, [location.search]);

  useEffect(() => {
    if (!formState.scanValue || loading) return;
    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current);
    }
    scanTimeoutRef.current = setTimeout(() => {
      handleSubmit();
    }, 220);
    return () => {
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
      }
    };
  }, [formState.scanValue]);

  const handleSubmit = async (event) => {
    if (loading) return;
    if (event) {
      event.preventDefault();
    }
    const eventIdValue = (eventId || attendance_default_event_id).toString().trim();
    if (!eventIdValue) {
      setStatus({ type: 'error', message: 'Event ID is missing.' });
      focusScanner();
      return;
    }
    const memberRegNo = parseMemberRegNo(formState.scanValue);
    if (!memberRegNo) {
      setStatus({ type: 'error', message: 'Scan a valid QR or enter a member ID.' });
      focusScanner();
      return;
    }

    setLoading(true);
    setStatus({ type: '', message: '' });
    try {
      const { data } = await casInstance.post(
        `${caversion}events/${eventIdValue}/attendance`,
        { member_reg_no: memberRegNo }
      );
      if (!data) {
        setStatus({ type: 'error', message: 'No response from server. Please try again.' });
        return;
      }
      if (data.success === false) {
        setStatus({ type: 'error', message: data.message || 'Attendance failed.' });
        return;
      }
      const responseData = data.data || data.payload || data;
      const entry = {
        id: Date.now(),
        eventCode: eventIdValue.toString().toUpperCase(),
        memberRegNo: responseData.member_reg_no || memberRegNo,
        name: responseData.name || responseData.fullName || responseData.member_name || '',
        email: responseData.email || '',
        mobile: responseData.mobile || '',
        imageUrl: responseData.imageUrl || responseData.photo || '',
        timestamp: getBangladeshTime()
      };

      setAttendee(entry);
      setCheckins((prev) => [entry, ...prev].slice(0, 8));
      setStatus({ type: 'success', message: 'Attendance successful.' });
      setLastScanAt(getBangladeshTime());
      setFormState((prev) => ({ ...prev, scanValue: '' }));
    } catch (error) {
      setStatus({ type: 'error', message: 'Unable to record attendance. Please try again.' });
    } finally {
      setLoading(false);
      focusScanner();
    }
  };

  return (
    <div className="booking-attendance-page" onClick={focusScanner}>
      <form className="attendance-scan-form" onSubmit={handleSubmit}>
        <label htmlFor="scanValue">Scan QR / Enter Member ID</label>
        <input
          id="scanValue"
          name="scanValue"
          type="text"
          inputMode="text"
          value={formState.scanValue}
          onChange={handleChange}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              handleSubmit();
            }
          }}
          ref={inputRef}
          onFocus={() => setScannerReady(true)}
          onBlur={() => setScannerReady(false)}
          autoComplete="off"
          aria-live="off"
        />
      </form>
      <section className="attendance-shell">
        <div className="attendance-card attendance-combined-card">
          <div className="attendance-event-header-card">
            {statsLoading && <div className="attendance-stats-loading">Loading event...</div>}
            {statsError && <div className="attendance-stats-error">{statsError}</div>}
            {!statsLoading && !statsError && stats && stats.open_event_details?.length > 0 && (
              <div className="attendance-event-card">
                <h1>{stats.open_event_details[0].name}</h1>
                <p>{stats.open_event_details[0].venue}</p>
              </div>
            )}
          </div>
          <div className="attendance-card-header">
            <div className="attendance-card-title">
              <h2>Attendance Profile</h2>
              <div className="attendance-scan-status">
                <span className={`attendance-scan-dot ${scannerReady ? 'ready' : 'idle'}`} />
                {scannerReady ? 'Scanner ready' : 'Click anywhere to focus scanner'}
              </div>
            </div>
            <span className="attendance-card-pill">ICAB Automation</span>
          </div>
          {status.message && (
            <div className={`attendance-status attendance-status-${status.type}`}>
              {status.message}
            </div>
          )}
          {loading && <div className="attendance-status attendance-status-loading">Saving attendance...</div>}
          <div className="attendance-profile">
            {attendee ? (
              <>
                <div className="attendance-profile-photo">
                  {attendee.imageUrl ? (
                    <img src={attendee.imageUrl} alt={attendee.name || 'Attendee'} />
                  ) : (
                    <span>{attendee.name ? attendee.name.charAt(0) : 'A'}</span>
                  )}
                </div>
                <div className="attendance-profile-info">
                  <h3>{attendee.name || 'Attendee'}</h3>
                  <p>{attendee.mobile || 'Mobile not available'}</p>
                  <p>{attendee.email || 'Email not available'}</p>
                </div>
              </>
            ) : (
              <div className="attendance-empty">
                <div className="attendance-empty-text">
                  Scan an ICAB member ID card to display the profile.
                </div>
                <div className="attendance-sample-card" aria-label="Sample ICAB ID card">
                  <div className="attendance-sample-band" aria-hidden="true" />
                  <div className="attendance-sample-body">
                    <div className="attendance-sample-left">
                      <div className="attendance-sample-avatar">A</div>
                      <div className="attendance-sample-badge">ICAB</div>
                    </div>
                    <div className="attendance-sample-meta">
                      <strong>Sample Member</strong>
                      <span>Scan QR to check-in</span>
                    </div>
                    <div className="attendance-sample-qr" aria-hidden="true" />
                  </div>
                  <div className="attendance-sample-footer">
                    <span>ICAB Attendance Automation</span>
                    <span>Valid for entry</span>
                  </div>
                </div>
              </div>
            )}
          </div>
          {lastScanAt && <div className="attendance-last-scan">Last scan: {lastScanAt} (BDT)</div>}
          <h2 className="attendance-log-title">Recent Check-ins</h2>
          <div className="attendance-log">
            {checkins.length === 0 && (
              <div className="attendance-empty">
                No entries yet.
              </div>
            )}
            {checkins.map((entry) => (
              <div className="attendance-log-item" key={entry.id}>
                <div>
                  <h3>{entry.name || 'Guest Attendee'}</h3>
                </div>
                <div className="attendance-log-meta">
                  <span>{entry.timestamp}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BookingAttendance;
